import {
  styled,
  Card,
  Input,
  Box,
  Text,
  Button,
  Link,
  Stack,
  Modal,
  Tab,
  Tabs,
  IconButton,
  Chip,
  SwitchLabel,
  Paper,
} from '@ltvco/refresh-lib/theme';
import { DatePicker, PickersDay } from '@mui/x-date-pickers';

// ============= BASE STYLES =============
const baseCardStyles = (theme: any) => ({
  backgroundColor: theme.palette.common.white,
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  borderRadius: theme.spacing(1),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
});

const baseButtonStyles = {
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: '32px',
  transition: 'all 0.2s ease-in-out',
} as const;

const baseInputStyles = {
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  border: '1px solid #E0E0E0',
  fontSize: '0.95rem',
  outline: 'none',
  overflow: 'hidden',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: '#0052FF',
    outline: 'none',
  },
  '&.Mui-focused, &:focus': {
    borderColor: '#0052FF',
    boxShadow: '0 0 0 2px rgba(0, 82, 255, 0.1)',
    outline: 'none',
  },
  '&:before, &:after': {
    display: 'none',
    content: 'none',
    border: 'none',
  },
} as const;

const baseTextStyles = {
  fontSize: { xs: '0.875rem', md: '1rem' },
} as const;

// Common responsive breakpoint styles
const responsiveContainerStyles = {
  flexDirection: 'column',
  '@media (min-width: 960px)': {
    flexDirection: 'row',
  },
} as const;

// Common padding styles
const basePaddingStyles = (theme: any) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
});

// Common small button styles
const baseSmallButtonStyles = {
  ...baseButtonStyles,
  fontSize: '0.75rem',
  padding: '4px 12px',
  height: '28px',
  minWidth: 'auto',
} as const;

// ============= CARD COMPONENTS =============

export const ResultCard = styled(Card)(({ theme }) => ({
  ...baseCardStyles(theme),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

export const InteractiveResultCard = styled(ResultCard)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
}));

export const SearchFormCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#F9F7FF',
  border: '0.5px solid',
  borderColor: theme.palette.text.primary,
  padding: '15px',
  boxShadow: theme.palette.background.light,
}));

// Disclaimer components
export const DisclaimerContainer: React.ComponentType<
  React.ComponentProps<typeof Box>
> = styled(Box)(({ theme }) => ({
  ...baseCardStyles(theme),
  marginTop: theme.spacing(4),
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1.5),
  borderColor: theme.palette.divider,
}));

export const DisclaimerText = styled(Text)(({ theme }) => ({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
}));

export const SearchInput = styled(Input)(({ theme }) => ({
  '& .MuiInputBase-root': {
    ...baseInputStyles,
    fontSize: '16px',
    fontWeight: 400,
    height: '50px',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      outline: 'none',
    },
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
      outline: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '13px 16px',
    '&::placeholder': {
      color: '#9E9E9E',
      opacity: 1,
    },
    '&:focus': {
      outline: 'none',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

// ============= LAYOUT COMPONENTS =============

// Result card layout components
export const ResultCardContainer = styled(Stack)(({ theme }) => ({
  ...responsiveContainerStyles,
  justifyContent: 'space-between',
  alignItems: 'stretch',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
  },
}));

export const ResultCardContent = styled(Box)({
  flex: 1,
});

export const ResultCardTitle = styled(Text)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.3rem',
  lineHeight: 1.3,
  marginBottom: theme.spacing(1),
}));

export const ResultCardSubtitle = styled(Text)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.text.primary,
  marginLeft: theme.spacing(1),
}));

// Search form styles
export const SearchFormContainer = styled(Stack)(({ theme }) => ({
  ...responsiveContainerStyles,
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
  },
}));

export const SearchFormInputContainer = styled(Box)({
  flex: 1,
});

export const SearchFormButtonContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: {
    width: '15%',
  },
}));

// Header and navigation styles
export const ResultsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export const ResultsHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
  },
}));

// Loading and error states
export const CenterContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  textAlign: 'center',
}));

// Select dropdown styles
export const sortSelectStyles = {
  minWidth: 80,
  maxWidth: 280,
  p: 0,
  height: 40,
  '& .MuiInputBase-root': {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      borderColor: 'primary.main',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    },
    '&.Mui-focused': {
      borderColor: 'primary.main',
      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
    },
  },
  '@media (max-width: 959px)': {
    minWidth: 'auto',
    maxWidth: '120px',
    '& .MuiInputBase-root': {
      fontSize: '0.85rem',
    },
  },
} as const;

// Search controls container styles
export const searchControlsContainerStyles = {
  display: 'flex',
  flexDirection: 'row',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: { xs: '100%', md: 'auto' },
  '@media (max-width: 599px)': {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 2,
  },
} as const;

// Exact match toggle styles
export const exactMatchToggleStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  padding: '10px 16px',
  backgroundColor: 'background.default',
  borderRadius: '8px',
  border: '1px solid',
  borderColor: 'divider',
  transition: 'all 0.2s ease-in-out',
  height: '52px',
  '@media (max-width: 959px)': {
    width: '100%',
    justifyContent: 'space-between',
    height: '52px',
    padding: '10px 10px 10px 16px',
    alignItems: 'center',
  },
  '@media (min-width: 960px)': {
    width: 'auto',
  },
  '@media (max-width: 599px)': {
    width: '100%',
  },
} as const;

export const exactMatchLabelStyles = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'text.primary',
  cursor: 'pointer',
  userSelect: 'none',
  '@media (max-width: 959px)': {
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
  },
} as const;

export const exactMatchLabelActiveStyles = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'primary.main',
  cursor: 'pointer',
  userSelect: 'none',
  '@media (max-width: 959px)': {
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
    color: '#0052FF',
  },
} as const;

// ============= OPINION COMPONENTS =============

// Base button styles for consistency
const baseRoundedButtonStyles = {
  ...baseButtonStyles,
  padding: '8px 24px',
  minWidth: '120px',
} as const;

export const ResultCardsContainer = styled(Box)(({ theme }) => ({
  ...basePaddingStyles(theme),
}));

export const SnippetContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

export const SnippetItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export const SnippetTitle = styled(Text)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.25),
}));

export const SnippetText = styled(Text)(() => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

// ============= BUTTON COMPONENTS =============

// Common button styles
export const paginationButtonStyles = {
  ...baseRoundedButtonStyles,
} as const;

export const ViewButton = styled(Button)(({ theme }) => ({
  ...baseRoundedButtonStyles,
  fontWeight: 600,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    minWidth: '100%',
    width: '100%',
  },
  [theme.breakpoints.up('md')]: {
    minWidth: '80px',
    width: 'auto',
  },
}));

// Opinion Details Header styles
export const OpinionDetailsCard = styled(Card)(({ theme }) => ({
  ...baseCardStyles(theme),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

export const OpinionDetailsMainContainer = styled(Stack)(({ theme }) => ({
  ...responsiveContainerStyles,
  gap: theme.spacing(3),
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    display: 'block',
  },
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

export const OpinionDetailsContentContainer = styled(Box)({
  flex: 1,
});

// Common clickable container styles
const baseClickableStyles = {
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
} as const;

export const OpinionDetailsClickableContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  ...baseClickableStyles,
  '&:hover': {
    ...baseClickableStyles['&:hover'],
    '& .case-name-text': {
      textDecoration: 'underline',
    },
  },
}));

export const OpinionDetailsCaseTitle = styled(Text)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.primary,
  fontSize: '2rem',
  lineHeight: 1.2,
}));

export const OpinionDetailsCourtContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  ...baseClickableStyles,
  '&:hover': {
    ...baseClickableStyles['&:hover'],
    '& .court-name-text': {
      textDecoration: 'underline',
    },
  },
}));

export const OpinionDetailsCourtName = styled(Text)(() => ({
  fontWeight: 500,
}));

export const OpinionDetailsDocketContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  ...baseClickableStyles,
  '&:hover': {
    ...baseClickableStyles['&:hover'],
    '& .docket-number-text': {
      textDecoration: 'underline',
    },
  },
}));

// Common icon styles
const baseIconStyles = (theme: any) => ({
  color: theme.palette.primary.main,
});

export const OpinionDetailsIcon = styled(Box)(({ theme }) => ({
  ...baseIconStyles(theme),
  fontSize: '1.5rem',
  marginTop: theme.spacing(-1),
}));

export const OpinionDetailsSmallIcon = styled(Box)(({ theme }) => ({
  ...baseIconStyles(theme),
  fontSize: '1.2rem',
  marginTop: theme.spacing(-1.5),
}));

export const OpinionDetailsTinyIcon = styled(Box)(({ theme }) => ({
  ...baseIconStyles(theme),
  fontSize: '0.9rem',
  marginTop: theme.spacing(-0.5),
}));

// Opinion content styles
export const OpinionContentContainer = styled(Box)(({ theme }) => ({
  ...baseCardStyles(theme),
  borderColor: theme.palette.grey[200],
  borderRadius: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  },
}));

export const OpinionContentHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const OpinionContentTitle = styled(Text)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.primary,
}));

export const OpinionContentHtml = styled(Box)(({ theme }) => ({
  ...baseCardStyles(theme),
  maxHeight: '500px',
  overflowY: 'auto',
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(2),
  borderColor: theme.palette.grey[200],
  fontSize: '0.875rem',
  lineHeight: 1.6,
  fontFamily: 'serif',
  color: theme.palette.text.primary,
}));

export const StyledDownloadButton = styled(Link)(({ theme }) => ({
  display: 'inline-flex !important',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1.25),
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: '20px',
  textDecoration: 'none',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
  transition: 'all 0.2s ease-in-out',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.text.primary,
    textDecoration: 'none',
    borderColor: theme.palette.grey[600],
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.16)',
  },
}));

// Opinion list container styles
export const OpinionListContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

export const OpinionListEmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

export const OpinionListEmptyText = styled(Text)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

// Common disclaimer styles
const baseDisclaimerStyles = (theme: any) => ({
  ...baseCardStyles(theme),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderColor: theme.palette.grey[300],
});

export const OpinionListDisclaimer = styled(Box)(({ theme }) => ({
  ...baseDisclaimerStyles(theme),
}));

export const OpinionListContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingLeft: 0,
  paddingRight: 0,
}));

// Opinion Summary styles
export const OpinionSummaryContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[300]}`,
}));

export const OpinionSummaryTitle = styled(Text)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

export const OpinionSummaryContent = styled(Text)(({ theme }) => ({
  lineHeight: 1.8,
  color: theme.palette.text.primary,
}));

// Similar cases styles
export const SimilarCasesContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

export const SimilarCasesEmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

export const SimilarCasesDisclaimer = styled(Box)(({ theme }) => ({
  ...baseDisclaimerStyles(theme),
}));

// Pagination overlay styles
export const PaginationOverlayContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: `${theme.palette.common.white}D9`, // 85% opacity in hex
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  backdropFilter: 'blur(2px)',
}));

export const paginationOverlayCardStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: 2,
  backgroundColor: 'common.white',
  borderRadius: 2,
  p: 3,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

// Opinion summary loading overlay styles
export const OpinionSummaryLoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: `${theme.palette.common.white}D9`, // 85% opacity in hex
  backdropFilter: 'blur(2px)',
  zIndex: 10,
}));

export const opinionSummaryLoadingCardStyles = {
  backgroundColor: 'common.white',
  borderRadius: 3,
  p: 4,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: 3,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  minWidth: { xs: '90%', sm: '400px' },
  maxWidth: { xs: '95%', sm: '500px' },
};

export const opinionSummaryLoadingIconStyles = {
  fontSize: '4rem',
  color: 'primary.main',
  animation: 'pulse 1.5s ease-in-out infinite',
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 0.6,
      transform: 'scale(1.05)',
    },
  },
};

export const opinionSummaryLoadingTitleStyles = {
  fontWeight: 600,
  textAlign: 'center' as const,
  color: 'text.primary',
};

export const opinionSummaryLoadingTextStyles = {
  textAlign: 'center' as const,
  color: 'text.secondary',
};

// No Results component styles
export const NoResultsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  textAlign: 'center',
  maxWidth: 600,
  margin: '0 auto',
}));

export const NoResultsIcon = styled(Box)(({ theme }) => ({
  fontSize: '5rem',
  color: theme.palette.text.secondary,
  opacity: 0.7,
}));

export const NoResultsTipsContainer = styled(Box)(({ theme }) => ({
  ...baseCardStyles(theme),
  width: '100%',
  maxWidth: 450,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
}));

export const NoResultsBullet = styled(Stack)(({ theme }) => ({
  width: '8px',
  color: theme.palette.primary.main,
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1,
}));

// Input-specific styles
export const advancedFilterInputStyles = {
  '& .MuiInputBase-root': {
    ...baseInputStyles,
    height: '44px',
  },
  '& .MuiInputBase-input': {
    padding: '10px 12px',
    '&:focus': {
      outline: 'none',
    },
  },
} as const;

export const advancedFilterMenuProps = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const,
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const,
  },
  PaperProps: {
    style: {
      maxHeight: 300,
    },
  },
};

// Select-specific styles
export const advancedFilterSelectStyles = {
  ...baseInputStyles,
  height: '44px',
  '& .MuiSelect-select': {
    padding: '10px 12px',
    '&:focus': {
      outline: 'none',
    },
  },
} as const;

export const getAdvancedFiltersToggleContainerStyles = (isOpen: boolean) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  mt: 1.5,
  mb: isOpen ? 1 : 2,
});

export const getAdvancedFiltersToggleButtonStyles = (isOpen: boolean) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  px: 2,
  py: 1,
  color: 'primary.main',
  backgroundColor: isOpen ? 'rgba(0, 82, 255, 0.25)' : 'rgba(0, 82, 255, 0.15)',
  border: isOpen
    ? '1px solid rgba(0, 82, 255, 0.5)'
    : '1px solid rgba(0, 82, 255, 0.3)',
  minWidth: { xs: '160px', md: 'auto' },
  '&:hover': {
    backgroundColor: isOpen
      ? 'rgba(0, 82, 255, 0.35)'
      : 'rgba(0, 82, 255, 0.25)',
    borderColor: isOpen ? 'rgba(0, 82, 255, 0.6)' : 'rgba(0, 82, 255, 0.5)',
  },
  transition: 'all 0.2s ease-in-out',
});

export const getAdvancedFiltersToggleIconStyles = (isOpen: boolean) => ({
  transition: 'transform 200ms ease',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
});

export const operatorsPanelStyles = {
  p: 2.5,
  border: '1px solid rgba(224, 224, 224, 0.5)',
  borderRadius: 2,
  backgroundColor: 'rgba(250, 250, 250, 0.6)',
  height: '360px',
  mt: { xs: 3, lg: 0 },
} as const;

export const clearButtonStyles = {
  ...baseButtonStyles,
  height: '48px',
  px: 3,
  color: 'text.secondary',
  borderColor: 'rgba(224, 224, 224, 0.74)',
  '&:hover': {
    borderColor: 'primary.main',
    backgroundColor: 'rgba(0, 82, 255, 0.04)',
  },
} as const;

export const filtersResultsTextStyles = {
  fontWeight: 600,
  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.375rem' },
  lineHeight: { xs: 1.4, sm: 1.3, md: 1.2 },
  wordBreak: 'break-word',
  hyphens: 'auto',
  maxWidth: { xs: '100%', sm: '90%', md: '100%' },
  pr: { xs: 0, sm: 1, md: 0 },
  mb: { xs: 1, md: 0.5 },
} as const;

// ============= FILTER STYLES =============

// Base list styles for filters and skeleton
const baseListStyles = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  maxWidth: '100%',
  gap: { xs: 1, md: 1 },
} as const;

export const filtersListStyles = {
  ...baseListStyles,
  mt: { xs: 0.5, md: 0 },
} as const;

// Base text styles for filter labels
const baseLabelTextStyles = {
  ...baseTextStyles,
  fontWeight: 500,
} as const;

export const filtersLabelTextStyles = {
  ...baseLabelTextStyles,
  color: 'text.secondary',
} as const;

// Base item container styles
const baseItemContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
} as const;

export const filtersItemContainerStyles = {
  ...baseItemContainerStyles,
  gap: { xs: 0.25, md: 0 },
} as const;

// Base text styles for filter items
const baseFilterTextStyles = {
  ...baseLabelTextStyles,
  fontWeight: 600,
  color: 'text.primary',
} as const;

export const filtersItemValueStyles = {
  ...baseFilterTextStyles,
  ml: 0.5,
  mr: { xs: 0.75, md: 1 },
  wordBreak: 'break-word',
} as const;

export const filtersSeparatorStyles = {
  color: 'text.disabled',
  mx: { xs: 0.25, md: 0.5 },
  fontSize: { xs: '1rem', md: '1.25rem' },
  lineHeight: 1,
} as const;

// Skeleton styles for loading states
const baseSkeletonStyles = {
  borderRadius: 1,
} as const;

export const skeletonResultsTextStyles = {
  ...baseSkeletonStyles,
  height: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
  width: { xs: '100%', sm: '60%', md: '50%' },
  mb: { xs: 1, md: 0.75 },
} as const;

export const skeletonSearchLabelStyles = {
  ...baseSkeletonStyles,
  height: { xs: '1rem', md: '1.125rem' },
  width: { xs: '100px', sm: '90px', md: '100px' },
} as const;

// Base skeleton styles for filter elements
const baseFilterSkeletonStyles = {
  height: { xs: '0.875rem', md: '1rem' },
  borderRadius: 0.5,
} as const;

export const skeletonFilterLabelStyles = {
  ...baseFilterSkeletonStyles,
  width: { xs: '50px', sm: '50px', md: '60px' },
} as const;

export const skeletonFilterValueStyles = {
  ...baseFilterSkeletonStyles,
  width: { xs: '80px', sm: '80px', md: '100px' },
} as const;

export const skeletonSeparatorStyles = {
  ...baseFilterSkeletonStyles,
  height: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
  width: { xs: '8px', sm: '10px', md: '12px' },
} as const;

export const skeletonListStyles = {
  ...baseListStyles,
  gap: { xs: 1, md: 1.25 },
} as const;

export const skeletonItemContainerStyles = {
  ...baseItemContainerStyles,
  gap: { xs: 0.5, md: 0.75 },
} as const;

// ============= CONTROL STYLES =============

// Common control container styles
const baseControlContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 16px',
  backgroundColor: 'background.default',
  borderRadius: '8px',
  border: '1px solid',
  borderColor: 'divider',
  transition: 'all 0.2s ease-in-out',
  height: '52px',
  '&:hover': {
    borderColor: 'primary.light',
    backgroundColor: 'action.hover',
  },
  '@media (max-width: 959px)': {
    width: '100%',
    padding: '10px 12px',
  },
  '@media (max-width: 599px)': {
    width: '100%',
  },
} as const;

export const sortByControlContainerStyles = {
  ...baseControlContainerStyles,
  gap: 2,
  '@media (max-width: 959px)': {
    ...baseControlContainerStyles['@media (max-width: 959px)'],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  '@media (min-width: 960px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
} as const;

// Text styles
export const sortByTextStyles = {
  fontSize: '1rem',
  fontWeight: 500,
  color: 'text.primary',
  '@media (max-width: 959px)': {
    fontSize: '0.9rem',
    marginBottom: '0',
    whiteSpace: 'nowrap',
  },
} as const;

// Switch styles
export const styledSwitchLabelStyles = {
  '& .MuiSwitch-root': {
    width: 42,
    height: 26,
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
  },
  '& .MuiSwitch-track': {
    borderRadius: 13,
  },
  '& .Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#0052FF',
  },
} as const;

export const StyledSwitchLabel = styled(SwitchLabel)(
  () => styledSwitchLabelStyles
);

export const JurisdictionModalStyled = styled(Modal)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '90vw',
    maxWidth: '900px',
    height: '80vh',
    maxHeight: '700px',
    margin: 'auto',
    borderRadius: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '95vw',
      height: '90vh',
      maxHeight: '90vh',
      borderRadius: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
}));

// ============= MODAL COMPONENTS =============

// Modal content container
export const ModalContentContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

// Modal header
export const ModalHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
  },
}));

// Modal body container
export const ModalBody = styled(Stack)({
  flex: 1,
  flexDirection: 'column',
  overflow: 'hidden',
});

// Tabs container
export const TabsContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    width: '24px',
    background:
      'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.9))',
    pointerEvents: 'none',
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));

// Styled tabs
export const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '44px',
  '& .MuiTabs-flexContainer': {
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
      width: 'max-content',
    },
  },
  '& .MuiTabs-indicator': {
    height: '3px',
    borderRadius: '2px 2px 0 0',
  },
  // Hide scroll buttons on desktop, only show on mobile
  '& .MuiTabs-scrollButtons': {
    display: 'none',
  },
  // Mobile-specific styles for tabs
  [theme.breakpoints.down('sm')]: {
    '& .MuiTabs-scrollButtons': {
      display: 'flex',
      width: '24px',
      opacity: 1,
      '&.Mui-disabled': {
        opacity: 0.3,
      },
    },
    '& .MuiTabs-scroller': {
      overflow: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      '-ms-overflow-style': 'none',
    },
  },
}));

// Styled tab
export const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.95rem',
  minHeight: '44px',
  padding: theme.spacing(1, 2.5),
  borderRadius: '8px 8px 0 0',
  transition: 'all 0.2s ease-in-out',
  marginRight: theme.spacing(0.5),
  '&.Mui-selected': {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    minHeight: '40px',
    minWidth: '80px',
    padding: theme.spacing(1, 1.5),
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: '6px',
    margin: theme.spacing(0.5, 0.5),
    '&.Mui-selected': {
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      borderColor: theme.palette.primary.light,
      backgroundColor: 'rgba(0, 82, 255, 0.04)',
      fontWeight: 700,
    },
  },
}));

// Mobile scroll indicator
export const TabScrollIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: '50%',
  width: '24px',
  height: '24px',
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

// Tab content container
export const TabContentContainer = styled(Stack)({
  flex: 1,
  flexDirection: 'column',
  overflow: 'hidden',
});

// Controls container (search + action buttons)
export const ControlsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
}));

// Search container
export const SearchContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  maxWidth: '300px',
  [theme.breakpoints.down('md')]: {
    maxWidth: 'none',
    width: '100%',
  },
}));

// Action buttons container
export const ActionButtonsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    justifyContent: 'space-between',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1),
    width: '100%',
    '& > *': {
      minWidth: 0,
    },
  },
}));

// Action button
export const ActionButton = styled(Button)(({ theme }) => ({
  ...baseButtonStyles,
  fontSize: '0.875rem',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: '20px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 0.75),
    whiteSpace: 'normal',
    textAlign: 'center',
    minHeight: '32px',
    borderRadius: '16px',
    boxShadow: 'none',
    border: '1px solid',
    borderColor: theme.palette.divider,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(0, 82, 255, 0.04)',
    },
    '&.Mui-disabled': {
      borderColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
    },
  },
}));

// Search input container for jurisdiction modal
export const SearchInputContainer = styled(Stack)(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    outline: 'none',
  },
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    outline: 'none',
  },
}));

// Jurisdiction search input (renamed to avoid conflict with main SearchInput)
export const JurisdictionSearchInput = styled(Input)(({ theme }) => ({
  flex: 1,
  '& .MuiInputBase-root': {
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
    '&:before, &:after': {
      display: 'none',
      content: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 6px 8px 2px',
    fontSize: '0.95rem',
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

// Search icon container
export const SearchIconContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 0.5),
  display: 'flex',
  alignItems: 'center',
}));

// Jurisdictions list container
export const JurisdictionsListContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(0.75, 0),
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 0,
  alignContent: 'flex-start',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.action.disabled,
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.action.active,
    },
  },
}));

// Jurisdiction item container
export const JurisdictionItemContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  padding: theme.spacing(0.35, 1.5),
  borderRadius: '8px',
  transition: 'background-color 0.1s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiCheckbox-root': {
    padding: theme.spacing(0.2),
    marginRight: theme.spacing(0.75),
  },
  '& .MuiFormControlLabel-root': {
    cursor: 'pointer',
    margin: 0,
    width: '100%',
    display: 'flex',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.35, 1),
    '& .MuiCheckbox-root': {
      marginRight: theme.spacing(0.5),
    },
  },
}));

// Modal footer
export const ModalFooter = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    alignItems: 'stretch',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },
}));

// Selected count display
export const SelectedCountContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
  },
}));

// Apply button
export const ApplyButton = styled(Button)(({ theme }) => ({
  ...baseRoundedButtonStyles,
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: theme.spacing(1.25, 2.5),
    minWidth: 'auto',
  },
}));

// Close button
export const CloseButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Trigger button (for AdvancedFilters) - styled to exactly match other advanced filter inputs
export const JurisdictionTriggerButton = styled(Button)(({ theme }) => ({
  ...baseInputStyles,
  textTransform: 'none',
  fontWeight: 400,
  color: theme.palette.text.primary,
  padding: '10px 12px',
  height: '44px',
  justifyContent: 'flex-start',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#FFFFFF',
    boxShadow: 'none',
  },
  '&.Mui-focused, &:focus': {
    backgroundColor: '#FFFFFF',
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}1A`,
  },
}));

// Pill/chip for showing selection count
export const SelectionPill = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  fontSize: '0.875rem',
  height: '24px',
  marginLeft: theme.spacing(1),
  '& .MuiChip-label': {
    padding: theme.spacing(0, 1),
  },
}));

// Empty state container
export const EmptyStateContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  gridColumn: '1 / -1',
  width: '100%',
  minHeight: '200px',
}));

// Docket entries text styles
export const descriptionTextStyles = {
  fontSize: '0.9rem',
  lineHeight: 1.4,
  display: 'block',
  maxWidth: '670px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  '@media (max-width: 600px)': {
    fontSize: '0.75rem',
    lineHeight: 1.4,
    maxWidth: '270px',
    fontWeight: 500,
  },
};

export const documentItemContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  mb: 0.7,
  '@media (max-width: 600px)': {
    gap: 0.25,
    px: 0.5,
  },
};

export const descriptionCellContentStyles = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
};

export const documentItemStyles = {
  display: 'flex',
  alignItems: 'center',
  pl: 0.25,
  '@media (max-width: 600px)': {
    pl: 0,
    flexWrap: 'wrap',
    borderTop: '1px dashed rgba(0,0,0,0.1)',
  },
};

export const attachmentTextStyles = {
  fontSize: '0.85rem',
  color: 'text.secondary',
  maxWidth: '645px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '@media (max-width: 600px)': {
    fontSize: '0.75rem',
    width: '100%',
    maxWidth: '270px',
    lineHeight: 1.2,
    wordBreak: 'break-word',
    display: 'block',
    whiteSpace: 'normal',
    mt: 1,
  },
};

export const noEntriesTextStyles = {
  py: 3,
  '@media (max-width: 600px)': {
    py: 2,
    fontSize: '0.875rem',
  },
};

// Document row styles for DocketEntriesTable
export const documentRowStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  py: 0.03125,
  '@media (max-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    gap: 1,
    py: 0.125,
  },
};

export const documentTitleContainerStyles = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  mt: 1,
  '@media (max-width: 600px)': {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
    order: 1,
  },
};

export const documentActionsContainerStyles = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  '@media (max-width: 600px)': {
    flexShrink: 0,
    order: 2,
    marginTop: '2px',
  },
};

export const downloadButtonGroupStyles = {
  display: 'flex',
  alignItems: 'center',
  borderRadius: '4px',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  '& > button:first-of-type': {
    borderRadius: '4px 0 0 4px',
    borderRight: 'none',
  },
  '& > button:last-of-type': {
    borderRadius: '0 4px 4px 0',
    borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
  },
  '@media (max-width: 600px)': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 'auto',
    marginTop: '2px',
  },
};

export const downloadButtonStyles = {
  ...baseSmallButtonStyles,
  borderRadius: 0,
  boxShadow: 'none',
  px: 1.5,
  '@media (max-width: 600px)': {
    height: '26px',
    fontSize: '0.7rem',
    px: 0.75,
    minWidth: '26px',
    '& .MuiButton-startIcon': {
      margin: 0,
    },
    '& .button-text': {
      display: 'none',
    },
  },
};

export const dropdownButtonStyles = {
  ...baseSmallButtonStyles,
  minWidth: '28px',
  px: 0.5,
  boxShadow: 'none',
  borderRadius: 0,
  mr: 0.25,
  '@media (max-width: 600px)': {
    height: '26px',
    width: '26px',
    minWidth: '26px',
  },
};

export const buyButtonStyles = {
  ...baseSmallButtonStyles,
  height: '24px',
  fontSize: '0.7rem',
  color: 'text.secondary',
  borderColor: 'rgba(224, 224, 224, 0.74)',
  px: 1,
  m: 0.25,
  '&:hover': {
    borderColor: 'primary.main',
    backgroundColor: 'rgba(0, 82, 255, 0.04)',
  },
  '& .button-icon': {
    display: 'none',
  },
  '& .button-text': {
    display: 'block',
    fontSize: '0.7rem',
  },
  '@media (max-width: 600px)': {
    height: '26px',
    px: 0.75,
    minWidth: '26px',
    whiteSpace: 'nowrap',
    m: 0.125,
    marginTop: '2px',
    '& .button-text': {
      display: 'none',
    },
    '& .button-icon': {
      display: 'block',
    },
  },
};

export const attachmentRowStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  py: 0.03125,
  '@media (max-width: 600px)': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    mb: 0.5,
    flexWrap: 'nowrap',
  },
};

export const attachmentTitleStyles = {
  flex: 1,
  '@media (max-width: 600px)': {
    flex: 1,
    minWidth: 0,
    order: 1,
  },
};

export const notAvailableStyles = {
  color: 'text.secondary',
  fontStyle: 'italic',
};

export const menuItemStyles = {
  fontSize: '0.75rem',
  py: 0.75,
  px: 1.5,
  minHeight: '32px',
  justifyContent: 'center',
  '@media (max-width: 600px)': {
    fontSize: '0.7rem',
    py: 1,
    px: 2,
    minHeight: '36px',
  },
};

//ADVANCED FILTERS COMPONENTS

export const AdvancedFiltersHeaderStack = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('lg')]: {
    width: '57%',
  },
}));

export const AdvancedFiltersHeaderBox = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    marginLeft: '8px !important',
  },
}));

// ============= PARTIES & ATTORNEYS COMPONENTS =============

export const partiesAttorneysContainerStyles = {
  py: { xs: 1, md: 1.5 },
  maxWidth: '1000px',
  mx: 'auto',
  px: { xs: 2, md: 3 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
} as const;

export const partiesGroupedContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 2, md: 3 },
  alignItems: 'center',
  width: '100%',
} as const;

export const partyTypeContainerStyles = {
  width: '100%',
  maxWidth: '900px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
} as const;

export const getPartyRowStyles = (isLastParty: boolean) => ({
  display: 'flex',
  py: { xs: 1.5, md: 2 },
  px: { xs: 1.5, md: 2.5 },
  borderBottom: !isLastParty ? '1px solid' : 'none',
  borderColor: 'grey.200',
  maxWidth: { xs: '100%', md: '800px' },
  mx: 'auto',
  width: '100%',
});

export const representedByLabelStyles = {
  fontStyle: 'italic',
  color: 'text.secondary',
  mb: { xs: 1, md: 1.5 },
  textTransform: 'uppercase',
  fontSize: { xs: '0.7rem', md: '0.75rem' },
  letterSpacing: '0.5px',
} as const;

export const attorneyStatusTextStyles = {
  fontStyle: 'italic',
  color: 'text.secondary',
  textTransform: 'uppercase',
  fontSize: { xs: '0.6rem', md: '0.65rem' },
  letterSpacing: '0.5px',
} as const;

export const partyTypeHeaderContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  ml: { xs: 1.5, md: 2.5 },
} as const;

export const partyTypeHeaderTextStyles = {
  fontWeight: 'bold',
  color: 'text.primary',
  mt: { xs: 1, md: 1.5 },
  fontSize: { xs: '1.1rem', md: '1.25rem' },
} as const;

export const partiesListContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  mb: 2,
  width: '100%',
} as const;

export const partyRowOverrideStyles = {
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { xs: 'flex-start', md: 'flex-start' },
} as const;

export const partyNameContainerStyles = {
  flex: { xs: '1 1 100%', md: '0 0 280px' },
  pr: { xs: 0, md: 6 },
  mb: { xs: 1.5, md: 0 },
  width: { xs: '100%', md: 'auto' },
} as const;

export const partyNameTextStyles = {
  fontWeight: 'medium',
  fontSize: { xs: '0.9rem', md: '0.875rem' },
  mb: { xs: 0.5, md: 0.25 },
} as const;

export const attorneyInfoContainerStyles = {
  flex: 1,
  ml: { xs: 0, md: 4 },
  width: { xs: '100%', md: 'auto' },
} as const;

export const attorneyNameTextStyles = {
  fontWeight: 'medium',
  mb: { xs: 0.25, md: 0.5 },
  fontSize: { xs: '0.85rem', md: '0.875rem' },
} as const;

export const attorneyPhoneTextStyles = {
  display: 'block',
  lineHeight: { xs: 1.2, md: 1.3 },
  mb: { xs: 0.125, md: 0.25 },
  fontSize: { xs: '0.7rem', md: '0.75rem' },
} as const;

export const attorneyContactTextStyles = {
  display: 'block',
  lineHeight: { xs: 1.2, md: 1.3 },
  fontSize: { xs: '0.7rem', md: '0.75rem' },
} as const;

export const noAttorneyTextStyles = {
  fontStyle: 'italic',
  fontSize: { xs: '0.8rem', md: '0.875rem' },
} as const;

// ============= TABLE COMPONENTS =============

export const TableContainer = styled(Paper)(({ theme }) => ({
  ...baseCardStyles(theme),
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.spacing(1),
    boxShadow: 'none',
    borderColor: theme.palette.divider,
  },
}));

export const tableOverflowContainerStyles = {
  overflowX: 'auto',
  width: '100%',
  '@media (max-width: 600px)': {
    overflowX: 'visible',
    width: '100%',
  },
};

// Base table styles
const baseTableStyles = {
  width: '100%',
  borderCollapse: 'collapse',
  borderSpacing: 0,
} as const;

export const StyledTable = styled('table')(({ theme }) => ({
  ...baseTableStyles,
  tableLayout: 'fixed',
  fontSize: '0.8rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    width: '100%',
    tableLayout: 'auto',
  },
}));

export const TableHead = styled('thead')(() => ({
  backgroundColor: '#f5f5f5',
}));

export const TableBody = styled('tbody')(() => ({
  '& tr:nth-of-type(even)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
}));

export const TableRow = styled('tr')(() => ({
  '&:hover': {
    backgroundColor: '#f9f9f9',
  },
  minHeight: '28px',
}));

// Base cell styles
const baseCellStyles = (theme: any) => ({
  borderBottom: '1px solid #e0e0e0',
  fontSize: '0.8rem',
  [theme.breakpoints.down('sm')]: {
    padding: `${theme.spacing(0.25)} ${theme.spacing(1.25)} ${theme.spacing(
      0.25
    )} ${theme.spacing(0.25)}`,
    fontSize: '0.8125rem',
  },
});

export const TableCell = styled('td')(({ theme }) => ({
  ...baseCellStyles(theme),
  padding: `${theme.spacing(0.375)} ${theme.spacing(0.0625)} ${theme.spacing(
    0.125
  )} ${theme.spacing(0.0625)}`,
}));

export const DateCell = styled(TableCell)(({ theme }) => ({
  minWidth: '180px',
  width: '180px',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  [theme.breakpoints.down('sm')]: {
    minWidth: '70px',
    width: '70px',
    whiteSpace: 'normal',
    lineHeight: 1.2,
    textAlign: 'left',
    padding: `${theme.spacing(0.25)} ${theme.spacing(1.5)} ${theme.spacing(
      0.25
    )} ${theme.spacing(0.25)}`,
  },
}));

export const DocumentNumberCell = styled(TableCell)(({ theme }) => ({
  minWidth: '120px',
  width: '120px',
  textAlign: 'center',
  verticalAlign: 'middle',
  [theme.breakpoints.down('sm')]: {
    minWidth: '30px',
    width: '30px',
    textAlign: 'left',
    padding: `${theme.spacing(0.25)} ${theme.spacing(1.5)} ${theme.spacing(
      0.25
    )} ${theme.spacing(0.25)}`,
  },
}));

export const DescriptionCell = styled(TableCell)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '645px',
  verticalAlign: 'middle',
  [theme.breakpoints.down('sm')]: {
    minWidth: '220px',
    maxWidth: '270px',
    wordBreak: 'break-word',
    padding: `${theme.spacing(0.25)} ${theme.spacing(0.25)} ${theme.spacing(
      0.25
    )} ${theme.spacing(0.25)}`,
  },
}));

export const DownloadButton = styled(Button)(({ theme }) => ({
  ...baseButtonStyles,
  padding: theme.spacing(0.2, 0.6),
  fontSize: '0.65rem',
  minWidth: 'auto',
  marginLeft: theme.spacing(2.5),
  height: '22px',
  borderRadius: '4px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: theme.spacing(0),
    marginTop: theme.spacing(0.75),
    fontSize: '0.6rem',
    padding: theme.spacing(0.1, 0.5),
    height: '20px',
    alignSelf: 'flex-start',
  },
}));

export const TableHeaderCell = styled('th')(({ theme }) => ({
  borderBottom: '1px solid #e0e0e0',
  fontSize: '0.8rem',
  textAlign: 'left',
  fontWeight: 'bold',
  backgroundColor: theme.palette.grey[100],
  padding: `${theme.spacing(0.625)} ${theme.spacing(1.25)} ${theme.spacing(
    0.375
  )} ${theme.spacing(1.25)}`,
  [theme.breakpoints.down('sm')]: {
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    fontSize: '0.8125rem',
  },
}));

export const DateHeaderCell = styled(TableHeaderCell)(({ theme }) => ({
  width: '180px',
  minWidth: '180px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '70px',
    width: '70px',
    textAlign: 'left',
    padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)} ${theme.spacing(
      0.375
    )} ${theme.spacing(1)}`,
  },
}));

export const DocumentNumberHeaderCell = styled(TableHeaderCell)(
  ({ theme }) => ({
    width: '120px',
    minWidth: '120px',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      minWidth: '35px',
      width: '35px',
      textAlign: 'left',
      fontSize: 0,
      lineHeight: 0,
      color: 'transparent',
      padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)} ${theme.spacing(
        0.375
      )} ${theme.spacing(1)}`,
    },
  })
);

export const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  background: theme.palette.background.paper,
  width: '100%',
  height: '50%',
  '.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '.MuiButtonBase-root': { color: theme.palette.primary.main },
  '.MuiPickersOutlinedInput-root': {
    height: '100%',
    boxSizing: 'border-box',
  },
}));

export const StyledPickersDay = styled(PickersDay)(({ theme }) => ({
  '&.MuiButtonBase-root.MuiPickersDay-root': {
    fontSize: theme.typography.body2.fontSize,
  },
  '&.MuiButtonBase-root.MuiPickersDay-root.Mui-selected': {
    backgroundColor: theme.palette.text.primary,
  },
}));

export const StackContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  p: 2,
  borderRadius: 1,
  border: `1px solid ${theme.palette.divider}`,
}));
